import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    Divider,
    FormControlLabel,
    Grid,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { Formik } from "formik";
import { useState } from "react";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";

import DefaultApiService from "../../../../../infrastructure/api/default";
import { wrapFormikHandleChange } from "../../../../../infrastructure/utils/formatters";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import DropdownPicker from "../../../../components/shared/dropdown";

const validationSchema = Yup.object().shape({});

const BasicSoftwareSettingsModifyPage = () => {
  const data = useLoaderData("root");
  let navigate = useNavigate();
  const { id: roleId } = useParams();

  const [loading, setLoading] = useState(false);

  const onNavigateToListPage = () => {
    navigate("/BI/Administration/BasicSoftwareSettings");
  };

  const buildSubmitPayload = (formValue) => {
    const { hasNeshanApiKey, hasNeshanServiceApiKey, ...fields } = formValue;

    return {
      id: roleId === "create" ? 0 : Number(data?.basicSoftwareSettings?.id ?? 0),
      ...fields,
      mapCenterLat: Number(fields.mapCenterLat) || 0,
      mapCenterLong: Number(fields.mapCenterLong) || 0,
      mapZoom: Number(fields.mapZoom) || 0,
      mapServiceKind: fields.mapServiceKind || "Default",
    };
  };

  // Submit Action
  const onSubmit = async (formValue, { setSubmitting }) => {
    setLoading(true);
    const sendData = buildSubmitPayload(formValue);

    try {
      const res = await DefaultApiService.modifyApi(
        "BI/Administration/BasicSoftwareSettings",
        sendData
      );

      if (res?.isSuccess) {
        toast.success(res.message || "عملیات با موفقیت انجام شد");
        onNavigateToListPage();
      } else {
        toast.error(res?.message || "ذخیره تنظیمات انجام نشد.");
      }

      setLoading(false);
      setSubmitting(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || "خطایی رخ داده است.");

      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12} md={12}>
        <Card>
          <CardHeader
            title={
              roleId !== "create"
                ? `ویرایش ${data?.basicSoftwareSettings?.date}`
                : "ایجاد"
            }
          />

          <CardContent>
            <Formik
              initialValues={{
                mapServiceKind: "Default",
                ...data.basicSoftwareSettings,
              }}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setFieldValue,
              }) => {
                const onMakeFormikVariable = (name) => ({
                  name: name,
                  fullWidth: true,
                  value: values[name],
                  error: errors[name] && touched[name],
                  helperText: errors[name] && touched[name] && errors[name],
                  variant: "outlined",
                });

                return (
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6} lg={6}>
                        <TextField
                          label="آدرس سرور اینترنت"
                          type="string"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("agentServerUrl")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={6}>
                        <TextField
                          label="آدرس لانچ نرم api"
                          type="string"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("appUrl")}
                        />
                      </Grid>
                      <Grid item xs={12} md={3} lg={3}>
                        <TextField
                          label="igmcUsername"
                          type="string"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("igmcUsername")}
                        />
                      </Grid>

                      <Grid item xs={12} md={3} lg={3}>
                        <TextField
                          label="igmcPassword"
                          type="string"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("igmcPassword")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="آدرس سرور پیامک"
                          type="string"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("smsServerUrl")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="آدرس سرور دیتالیک"
                          type="string"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("esbServerUrl")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <FormControlLabel
                          name="isDirectInternet"
                          value={values.isDirectInternet}
                          checked={values.isDirectInternet}
                          onChange={wrapFormikHandleChange(handleChange)}
                          control={<Switch />}
                          label="اینترنت مستقیم"
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <FormControlLabel
                          name="isActiveLogger"
                          value={values.isActiveLogger}
                          checked={values.isActiveLogger}
                          onChange={wrapFormikHandleChange(handleChange)}
                          control={<Switch />}
                          label="لاگر فعال شود"
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="مرکز نقشه lat"
                          type="string"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("mapCenterLat")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="مرکز نقشه long"
                          type="string"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("mapCenterLong")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="زوم نقشه"
                          type="string"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("mapZoom")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="آدرس URL نقشه نشان"
                          type="string"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("googleMapUrl")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="آدرس URL نقشه ماهواره‌ای"
                          type="string"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("googleEarthUrl")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <DropdownPicker
                          label="سرویس نقشه"
                          onChange={(event) => {
                            setFieldValue("mapServiceKind", event.target.value);
                          }}
                          list={data.mapServiceKind}
                          filterable={false}
                          {...onMakeFormikVariable("mapServiceKind")}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                          تنظیمات نقشه نشان
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                          نشان برای «نمایش نقشه» و «جستجو/Reverse» کلید جدا می‌خواهد. Search API ممکن است نیاز به فعال‌سازی از طریق پشتیبانی نشان داشته باشد.
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="کلید API نقشه (Web SDK)"
                          type="password"
                          autoComplete="new-password"
                          placeholder={values.hasNeshanApiKey ? values.neshanApiKey : "کلید Web Map را وارد کنید"}
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("neshanApiKey")}
                          helperText="برای نمایش نقشه در مرورگر — نوع کلید: Web"
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="کلید API سرویس (Search / Reverse)"
                          type="password"
                          autoComplete="new-password"
                          placeholder={
                            values.hasNeshanServiceApiKey
                              ? values.neshanServiceApiKey
                              : "کلید سرویس Search را وارد کنید"
                          }
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("neshanServiceApiKey")}
                          helperText={
                            values.hasNeshanServiceApiKey
                              ? "کلید سرویس ذخیره شده. برای تغییر، کلید جدید وارد کنید."
                              : "برای جستجوی آدرس — از پنل نشان با سرویس Search دریافت کنید."
                          }
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={12}
                        container
                        justifyContent={"flex-end"}
                      >
                        <Button
                          disabled={isSubmitting}
                          variant="outlined"
                          size="large"
                          color="gray"
                          sx={{ mx: 1 }}
                          onClick={onNavigateToListPage}
                        >
                          انصراف
                        </Button>

                        <Button
                          disabled={isSubmitting}
                          variant="contained"
                          type="submit"
                          size="large"
                        >
                          {loading && (
                            <CircularProgress size={16} color="inherit" />
                          )}
                          {roleId !== "create" ? "ویرایش" : "ایجاد"}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                );
              }}
            </Formik>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default BasicSoftwareSettingsModifyPage;
